/**
 * connect/recommendations.ts — pure smart-recommendation rules.
 *
 * Every recommendation derives from RECORDED data (plans, city facts, speed
 * reports, places) with an explicit basis. Nothing is invented — no made-up
 * prices, outage forecasts, or traffic conditions. Spec flavor examples like
 * "airport rail is faster than a taxi right now" become possible only when a
 * future provider records such data.
 */

import type {
  CityConnectivityFacts,
  ConnectPlace,
  ConnectRecommendation,
  ConnectivityProfile,
  DataPlan,
  SpeedReport,
} from './models';
import { computeInternetIntelligence } from './intelligence';

const DAY = 24 * 60 * 60 * 1000;

export function computeRecommendations(input: {
  profile: ConnectivityProfile;
  plans: DataPlan[];
  cityFacts: CityConnectivityFacts[];
  speedReports: SpeedReport[];
  places: ConnectPlace[];
  currentCity: string | null;
  now: number;
}): ConnectRecommendation[] {
  const { profile, plans, cityFacts, speedReports, places, currentCity, now } = input;
  const recommendations: ConnectRecommendation[] = [];

  // Active plan expiring soon.
  for (const plan of plans) {
    if (!plan.active || plan.expiresAt == null) continue;
    const daysLeft = Math.floor((plan.expiresAt - now) / DAY);
    if (daysLeft < 0) {
      recommendations.push({
        id: `plan-expired-${plan.id}`,
        message: `Your ${plan.provider} ${plan.name} plan has expired — activate a replacement.`,
        basis: [`plan expiry ${new Date(plan.expiresAt).toISOString().slice(0, 10)}`],
        severity: 'important',
      });
    } else if (daysLeft <= 3) {
      recommendations.push({
        id: `plan-expiring-${plan.id}`,
        message: daysLeft === 0
          ? `Your ${plan.provider} ${plan.name} plan expires today.`
          : `Your ${plan.provider} ${plan.name} plan expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`,
        basis: [`plan expiry ${new Date(plan.expiresAt).toISOString().slice(0, 10)}`],
        severity: daysLeft <= 1 ? 'important' : 'nudge',
      });
    }
  }

  // A cheaper recorded alternative to the active plan (same country + currency).
  const active = plans.filter((p) => p.active && p.priceMonthly != null);
  for (const plan of active) {
    const cheaper = plans
      .filter((p) =>
        !p.active && p.id !== plan.id && p.country === plan.country &&
        p.currency === plan.currency && p.priceMonthly != null && p.priceMonthly < plan.priceMonthly!)
      .sort((a, b) => a.priceMonthly! - b.priceMonthly!)[0];
    if (cheaper) {
      recommendations.push({
        id: `cheaper-plan-${plan.id}-${cheaper.id}`,
        message: `${cheaper.provider} ${cheaper.name} (${cheaper.currency} ${cheaper.priceMonthly}) is cheaper than your current ${plan.provider} plan (${plan.currency} ${plan.priceMonthly}).`,
        basis: [`recorded prices: ${cheaper.provider} ${cheaper.priceMonthly}, ${plan.provider} ${plan.priceMonthly}`],
        severity: 'info',
      });
    }
  }

  if (currentCity != null) {
    const facts = cityFacts.find((f) => f.city === currentCity) ?? null;

    // Fiber recorded as available in the current city.
    if (facts?.fiberAvailable === true) {
      recommendations.push({
        id: `fiber-available-${currentCity}`,
        message: `Fiber internet is recorded as available in ${currentCity} — consider a fixed line for remote work.`,
        basis: [`city facts for ${currentCity}: fiberAvailable`],
        severity: 'info',
      });
    }

    // Power reliability concerns recorded → suggest recorded coworking backup.
    if (facts?.powerReliabilityNote != null) {
      const coworking = places.find((p) => p.city === currentCity && p.kind === 'coworking');
      recommendations.push({
        id: `power-note-${currentCity}`,
        message: coworking
          ? `Power note for ${currentCity}: "${facts.powerReliabilityNote}" — ${coworking.name} is a recorded coworking backup.`
          : `Power note for ${currentCity}: "${facts.powerReliabilityNote}" — record a coworking backup option.`,
        basis: [`city facts for ${currentCity}: powerReliabilityNote`],
        severity: 'nudge',
      });
    }

    // Recorded average speed below the user's stated requirement.
    if (profile.minInternetMbps != null) {
      const intel = computeInternetIntelligence(currentCity, speedReports, facts);
      if (intel.averageDownloadMbps != null && intel.averageDownloadMbps < profile.minInternetMbps) {
        recommendations.push({
          id: `speed-below-need-${currentCity}`,
          message: `Recorded average speed in ${currentCity} (${Math.round(intel.averageDownloadMbps)} Mbps over ${intel.reportCount} report${intel.reportCount === 1 ? '' : 's'}) is below your ${profile.minInternetMbps} Mbps requirement — plan a backup.`,
          basis: [`${intel.reportCount} recorded speed reports`, `minInternetMbps ${profile.minInternetMbps}`],
          severity: 'important',
        });
      }
    }

    // Roaming with no local plan recorded for the current country.
    if (profile.roamingStatus === 'roaming') {
      recommendations.push({
        id: `roaming-${currentCity}`,
        message: `You are roaming in ${currentCity} — compare local SIM/eSIM plans to cut costs.`,
        basis: ['profile roamingStatus: roaming'],
        severity: 'nudge',
      });
    }
  }

  return recommendations;
}
